import CloseOutlined from '@mui/icons-material/CloseOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { flushSync } from 'react-dom';
import { useClients } from '../../lib/filters.hooks.js';
import { useRunResult, useWorkflowDetail } from '../../lib/workflows.hooks.js';
import { PrintBaseStyles, printPage } from '../../print/PagePrintAction.js';
import { downloadCsv } from '../creatives/data/csv.js';
import { BriefPager, BriefPrintout, briefed } from './BriefCard.js';
import { BriefMark } from './BriefMark.js';
import { CopyLinkButton } from './CopyLinkButton.js';
import { CreativeDrawer } from './CreativeDrawer.js';
import { StoryboardDrawer } from './ideation/storyboard/StoryboardDrawer.js';
import { reportRowsToCsv } from './outcomeRows.js';
import { type ParsedResult, parseOutcome } from './parsedResult.js';
import { ReportHeadline, ReportRecommendationLine, ReportSection } from './ReportHeadline.js';
import { ReportMark } from './ReportMark.js';
import { ResultView } from './ResultView.js';
import { type ReportScope, reportMetaParts } from './reportMeta.js';
import { type ReportSummary, reportSummaryOf } from './reportSummary.js';
import { summaryOf } from './resultRows.js';
import { ShareRunButton } from './ShareRunButton.js';
import type { MapOutcome } from './sessionRows.js';
import type { WorkflowRun } from './workflows.types.js';

const DRAWER_WIDTH = 900;
// Printing the drawer is the download: the page behind it drops out and the
// drawer flows onto paper instead of scrolling inside the viewport.
const PRINT_DRAWER_ONLY = {
  '@media print': {
    '#root, .MuiBackdrop-root, .MuiDrawer-root:not(.outcome-drawer)': {
      display: 'none !important',
    },
    'html, body': { overflow: 'visible !important', height: 'auto !important' },
    '.outcome-drawer, .outcome-drawer > .MuiDrawer-paper': {
      position: 'static !important',
      width: '100% !important',
      height: 'auto !important',
      overflow: 'visible !important',
    },
  },
};

interface OutcomeDrawerProps {
  outcome: MapOutcome | null;
  scope: ReportScope | null;
  leadingKpi: string;
  // Which brief to open on. A run holds one per creative, so opening from a
  // creative's own card has to land on that creative's brief.
  focus?: string | null;
  onClose: () => void;
}

// The drawer holds the outcome, not the action that made it: "Create Brief" is
// the step you ran, and what you opened is the brief itself.
function heading(outcome: MapOutcome): string {
  if (outcome.kind === 'brief') return 'Brief details';
  return outcome.date ? `${outcome.name} ${outcome.date}` : outcome.name;
}

// Opens on the outcomes page for anyone entitled to the run's accounts.
function briefLink(runId: string, creativeId: string | undefined): string {
  const search = new URLSearchParams({ openRunId: runId });
  if (creativeId !== undefined) search.set('openBrief', creativeId);
  return `${window.location.origin}/outcomes?${search.toString()}`;
}

function ReportBody({
  run,
  report,
  resultData,
  onOpenCreative,
  scope,
  leadingKpi,
}: {
  run: WorkflowRun;
  report: ReportSummary | null;
  resultData: ParsedResult;
  onOpenCreative: (creativeId: string, networkId: number) => void;
  scope: ReportScope | null;
  leadingKpi: string;
}): ReactNode {
  const creativeDetail = scope === null ? {} : { creativeDetail: { scope, leadingKpi } };
  const summary = summaryOf(run.result);
  if (report === null) {
    // A comparison shows its views whether or not any creative made the pool.
    const empty =
      summary === null && resultData.kind === 'other' && run.resultKind !== 'comparison';
    return empty ? null : (
      <>
        {summary === null ? null : (
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{summary}</Typography>
        )}
        <ResultView
          run={run}
          resultData={resultData}
          onOpenCreative={onOpenCreative}
          {...creativeDetail}
        />
      </>
    );
  }

  return (
    <>
      <ReportHeadline
        headline={report.headline}
        detail={report.detail ?? summary}
        tiles={report.tiles}
      />
      <ReportSection title={report.section} />
      <ResultView
        run={run}
        resultData={resultData}
        onOpenCreative={onOpenCreative}
        {...creativeDetail}
      />
    </>
  );
}

export function OutcomeDrawer({
  outcome,
  scope,
  focus = null,
  leadingKpi,
  onClose,
}: OutcomeDrawerProps): ReactNode {
  const [picked, setPicked] = useState<string | null>(null);
  const [scored, setScored] = useState<{ creativeId: string; networkId: number } | null>(null);
  const [printing, setPrinting] = useState(false);
  const openScored = (creativeId: string, networkId: number): void =>
    setScored({ creativeId, networkId });
  const run = useRunResult(outcome?.runId ?? null);
  const { data: clients } = useClients();
  const record = run.data ?? null;
  const finished = record !== null && record.status === 'completed' ? record : null;
  const shareable = useWorkflowDetail(finished?.actionType ?? null).data?.shareable ?? false;
  const resultData =
    finished === null
      ? null
      : parseOutcome(
          finished.result,
          finished.resultKind,
          outcome?.kind ?? null,
          finished.outcomeKind,
        );
  const handleDownload = async (title?: string): Promise<void> => {
    // The print styles must be committed before printPage reaches window.print.
    flushSync(() => setPrinting(true));
    try {
      await printPage(title);
    } finally {
      setPrinting(false);
    }
  };
  const close = (): void => {
    setPicked(null);
    setScored(null);
    onClose();
  };
  if (resultData?.kind === 'storyboard' && resultData.parsed.success && finished !== null) {
    return (
      <>
        {printing ? (
          <>
            <PrintBaseStyles />
            <GlobalStyles styles={PRINT_DRAWER_ONLY} />
          </>
        ) : null}
        <StoryboardDrawer
          key={outcome?.runId}
          className="outcome-drawer"
          result={resultData.parsed.data}
          initialVersion={0}
          open={outcome !== null}
          onClose={close}
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => void handleDownload(outcome?.name)}
                disabled={printing}
                startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
              >
                Download
              </Button>
              {shareable ? <ShareRunButton runId={finished.runId} /> : null}
            </>
          }
        />
      </>
    );
  }
  const meta = reportMetaParts(
    scope,
    (clients ?? []).flatMap((client) => client.accounts),
  );
  const rows = resultData?.kind === 'other' ? resultData.rows : [];
  const report = rows.length === 0 ? null : reportSummaryOf(rows);
  const pages =
    finished === null || finished.resultKind !== 'brief' || resultData?.kind === 'ideation_brief'
      ? []
      : briefed(finished);
  // `picked` is the reader's own paging; `focus` is where the drawer was opened.
  const openOn = picked ?? focus;
  const at = Math.max(
    0,
    pages.findIndex((page) => page.brief.creativeId === openOn),
  );
  const firstBrief = pages[0]?.brief.name;
  const printTitle =
    firstBrief === undefined
      ? outcome?.name
      : pages.length === 1
        ? firstBrief
        : `${firstBrief} and ${pages.length - 1} more`;

  return (
    <Drawer
      anchor="right"
      open={outcome !== null}
      onClose={close}
      slotProps={{
        paper: {
          sx: { width: `min(${DRAWER_WIDTH}px, 100vw)`, bgcolor: 'background.page' },
        },
      }}
      className="outcome-drawer"
      data-testid="outcome-drawer"
    >
      {outcome === null ? null : (
        <Stack sx={{ height: '100%', '@media print': { height: 'auto' } }}>
          {printing ? (
            <>
              <PrintBaseStyles />
              <GlobalStyles styles={PRINT_DRAWER_ONLY} />
            </>
          ) : null}
          <Stack
            direction="row"
            sx={{
              gap: 2,
              px: 6,
              py: 4,
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              // A printed brief heads each page with its own name.
              ...(pages.length === 0 ? {} : { displayPrint: 'none' }),
            }}
          >
            {outcome.kind === 'brief' ? <BriefMark /> : <ReportMark />}
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {pages[at]?.brief.name ?? heading(outcome)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {meta.join(' · ')}
              </Typography>
            </Stack>
            <IconButton
              onClick={close}
              aria-label="Close this outcome"
              sx={{ displayPrint: 'none' }}
            >
              <CloseOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Stack
            sx={{
              flex: 1,
              gap: 4,
              px: 6,
              py: 5,
              overflowY: 'auto',
              '@media print': { overflowY: 'visible' },
            }}
          >
            {run.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            ) : null}
            {run.isError ? <Alert severity="error">This outcome could not be read.</Alert> : null}
            {finished === null || resultData === null ? null : finished.resultKind === 'brief' &&
              resultData.kind !== 'ideation_brief' ? (
              printing ? (
                <BriefPrintout pages={pages} meta={meta.join(' · ')} />
              ) : (
                <BriefPager
                  pages={pages}
                  at={at}
                  onSelect={(index) => setPicked(pages[index]?.brief.creativeId ?? null)}
                  {...(scope === null
                    ? {}
                    : {
                        creativeDetail: {
                          workflowId: finished.actionType,
                          scope,
                          leadingKpi,
                        },
                      })}
                />
              )
            ) : (
              <ReportBody
                run={finished}
                report={report}
                resultData={resultData}
                onOpenCreative={openScored}
                scope={scope}
                leadingKpi={leadingKpi}
              />
            )}
          </Stack>

          <Stack
            direction="row"
            sx={{
              gap: 4,
              px: 6,
              py: 3,
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            {outcome.kind === 'brief' ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Brief created and saved to outcomes
              </Typography>
            ) : report?.recommendation === undefined || report.recommendation === null ? (
              <Box />
            ) : (
              <ReportRecommendationLine recommendation={report.recommendation} />
            )}
            <Stack direction="row" sx={{ gap: 2, flexShrink: 0, displayPrint: 'none' }}>
              {finished === null ? null : (
                <Button
                  variant="outlined"
                  onClick={() => void handleDownload(printTitle)}
                  disabled={printing}
                  startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
                >
                  Download
                </Button>
              )}
              {rows.length === 0 ? null : (
                <Button
                  variant="outlined"
                  onClick={() => downloadCsv(`${outcome.name}.csv`, reportRowsToCsv(rows))}
                  startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
                >
                  Export CSV
                </Button>
              )}
              {shareable && finished !== null ? <ShareRunButton runId={finished.runId} /> : null}
              {finished === null || pages.length === 0 ? null : (
                <CopyLinkButton url={briefLink(finished.runId, pages[at]?.brief.creativeId)} />
              )}
              <Button variant="outlined" onClick={close}>
                Close
              </Button>
            </Stack>
          </Stack>
          {scored === null || finished === null ? null : (
            <CreativeDrawer
              open
              runId={finished.runId}
              result={finished.result}
              creativeId={scored.creativeId}
              networkId={scored.networkId}
              onOpenCreative={openScored}
              onClose={() => setScored(null)}
            />
          )}
        </Stack>
      )}
    </Drawer>
  );
}
